import { isEmpty, isNil } from 'ramda';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import SlideComponent from '~/components/atom/SlideComponent';
import TextButton from '~/components/atom/TextButton';
import { Body } from '~/components/atom/Typography';
import { SIDE_MIN_WIDTH } from '~/components/organism/Navigation/constants';
import {
  withRouterTransitionContext,
  type RouterTransitionContextProps,
} from '~/contexts/RouterTransitionContext';

const text = {
  cancel: 'Annuleren',
  save: 'Opslaan',
  saveText: (numberOfChanges?: number): string =>
    `Je hebt${
      numberOfChanges == null || numberOfChanges === 0
        ? ''
        : ` ${numberOfChanges}`
    } gewijzigde items op deze pagina`,
};

export type Props = {
  dataTestId?: string;
  className?: string;

  /** Show/hide the bar */
  visible?: boolean;
  /** Distance from bottom in px */
  bottom?: number;
  /** Upper bound for bar width (also capped by breakpoint/viewport/anchor) */
  maxWidth?: number;
  /** Lower bound for bar width to keep content usable */
  minWidth?: number;
  /** Anchor element to align and clamp to (e.g., centered content container) */
  anchorRef?: React.RefObject<HTMLElement | HTMLDivElement>;
  /** The number of changes that are pending saving */
  numberOfChanges?: number;

  errorMessage?: string | React.ReactNode;

  actionProps: {
    onCancel: () => void;
    onSave: () => void;
    loading?: boolean;
    disabled?: boolean;
  };
} & { routerProps?: RouterTransitionContextProps };

const DEFAULT_MAX_WIDTH = 900;
const DEFAULT_MIN_WIDTH = 100;
export const DEFAULT_BOTTOM = 40;

const FloatingSaveBar: React.FC<Props> = ({
  dataTestId,
  className,
  visible = true,
  bottom = DEFAULT_BOTTOM,
  maxWidth = DEFAULT_MAX_WIDTH,
  minWidth = DEFAULT_MIN_WIDTH,
  anchorRef,
  numberOfChanges,
  errorMessage,
  actionProps,
  routerProps,
}) => {
  const theme = useTheme();
  // Horizontal translation from the viewport center.
  // Positive values move the bar to the right.
  const [offset, setOffset] = useState(0);
  // Dynamic max-width after clamping to breakpoint/viewport/anchor.
  const [computedMaxWidth, setComputedMaxWidth] = useState(maxWidth);

  useEffect(() => {
    if (!routerProps?.leaveHandler) return;

    const { leaveHandler } = routerProps;

    if (visible) {
      leaveHandler(false);
      return () => leaveHandler(true);
    }

    // Ensure no reload/leave prompt when the bar is hidden
    leaveHandler(true);
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, routerProps?.leaveHandler]);

  useLayoutEffect(() => {
    const update = () => {
      const navWidth = theme.remToPxRaw(SIDE_MIN_WIDTH);
      const pageGutter = theme.remToPxRaw(theme.space('m')) * 2;
      const viewportUsable = Math.max(
        window.innerWidth - navWidth - pageGutter,
        0,
      );

      if (anchorRef?.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const anchorCenter = rect.left + rect.width / 2;
        const viewportCenter = window.innerWidth / 2;
        setOffset(anchorCenter - viewportCenter);

        // Pick a width that's not too big and not too small:
        // - Not too big: it must fit inside the anchor box, the free screen space, and our max size.
        // - Not too small: it must be at least the minimum size.
        // So we take the smallest of the big limits, then make sure it's not smaller than the minimum.
        const next = Math.max(
          minWidth,
          Math.min(rect.width, viewportUsable, maxWidth),
        );
        setComputedMaxWidth(next);
        return;
      }

      // no anchor: visually center over content by shifting half nav width
      setOffset(navWidth / 2);

      const next = Math.max(minWidth, Math.min(viewportUsable, maxWidth));
      setComputedMaxWidth(next);
    };

    update();
    window.addEventListener('resize', update);

    let ro: ResizeObserver | null = null;
    if (anchorRef?.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => update());
      ro.observe(anchorRef.current);
    }

    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRef, maxWidth, minWidth]);

  if (!visible) return null;

  const content = getContent({ numberOfChanges, errorMessage });

  return (
    <Container
      data-testid={dataTestId}
      className={className}
      $bottom={bottom}
      $maxWidth={computedMaxWidth}
      $minWidth={minWidth}
      $offset={offset}
    >
      <SlideComponent isVisible={visible} direction="top" width="100%">
        <JustificationContainer
          width="100%"
          justification="space-between"
          align="center"
          backgroundColor={{ group: 'tertiary' }}
          padding={['base', 'l']}
          border={{ radius: 'm' }}
          boxShadow="around"
        >
          <JustificationContainer width="100%" align="center">
            <Body
              margin={[null]}
              color={{ group: 'secondary', variant: 'dark' }}
              size="base"
            >
              {content}
            </Body>
          </JustificationContainer>
          <JustificationContainer width="max-content" align="center" gap="base">
            <TextButton
              label={text.cancel}
              appearance="danger"
              onClick={actionProps.onCancel}
            />
            <Button
              label={text.save}
              appearance="secondary"
              icon="save"
              onClick={actionProps.onSave}
              loading={actionProps.loading}
              disabled={actionProps.disabled}
            />
          </JustificationContainer>
        </JustificationContainer>
      </SlideComponent>
    </Container>
  );
};

/**
 * Fixed to viewport bottom, horizontally centered via translate from 50%.
 * We add a dynamic X-offset to compensate for left navigation or align to an anchor.
 */
const Container = styled.div<{
  $bottom: number;
  $maxWidth: number;
  $minWidth: number;
  $offset: number;
}>(
  ({ theme, $bottom, $maxWidth, $minWidth, $offset }) => css`
    position: fixed;
    left: 50%;
    transform: translateX(calc(-50% + ${$offset}px));
    bottom: ${$bottom}px;

    width: 100%;
    max-width: ${$maxWidth}px;
    min-width: ${$minWidth}px;
    z-index: ${theme.getTokens().zIndex.top};

    transition:
      bottom 0.2s ease-out,
      max-width 0.2s ease-out,
      transform 0.2s ease-out;
  `,
);

const getContent = ({
  numberOfChanges,
  errorMessage,
}: {
  numberOfChanges?: number;
  errorMessage?: string | React.ReactNode;
}): string | null | React.ReactNode => {
  if (!isNil(errorMessage) && !isEmpty(errorMessage)) return errorMessage;

  if (!isNil(numberOfChanges) && numberOfChanges > 0)
    return text.saveText(numberOfChanges);

  return null;
};

export default withRouterTransitionContext(FloatingSaveBar);
